import { createAuthClient } from "better-auth/client";
import {
  twoFactorClient,
  organizationClient,
  adminClient,
  magicLinkClient,
  genericOAuthClient,
  jwtClient,
} from "better-auth/client/plugins";

export interface AuthClientConfig {
  baseURL: string;
}

export interface AuthClientError {
  code?: string;
  message?: string;
  status?: number;
  [key: string]: unknown;
}

export interface AuthClientResult<TData = unknown> {
  data?: TData;
  error?: AuthClientError | null;
  [key: string]: unknown;
}

export type AuthClientMethod<TResult = AuthClientResult> = (
  ...args: unknown[]
) => Promise<TResult>;

export type AuthClientNamespace = Record<string, AuthClientMethod>;

export interface AuthSessionData {
  session?: {
    token?: string;
    [key: string]: unknown;
  } | null;
  user?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface SignInClient extends AuthClientNamespace {
  email: AuthClientMethod;
  magicLink: AuthClientMethod;
  social: AuthClientMethod;
}

export interface SignUpClient extends AuthClientNamespace {
  email: AuthClientMethod;
}

export interface OrganizationClient extends AuthClientNamespace {
  list: AuthClientMethod;
  setActive: AuthClientMethod;
}

export interface AuthClient {
  signIn: SignInClient;
  signUp: SignUpClient;
  signOut: AuthClientMethod;
  getSession: AuthClientMethod<AuthClientResult<AuthSessionData>>;
  listSessions: AuthClientMethod;
  updateUser: AuthClientMethod;
  revokeSession: AuthClientMethod;
  twoFactor: AuthClientNamespace;
  organization: OrganizationClient;
  admin: AuthClientNamespace;
}

export function createEaseAuthClient(config: AuthClientConfig): AuthClient {
  // Better Auth 1.6 infers zod-heavy implementation types that are not
  // declaration-portable. Keep this package's public boundary stable.
  return createAuthClient({
    baseURL: config.baseURL,
    basePath: "/auth",
    plugins: [
      jwtClient(),
      twoFactorClient(),
      organizationClient(),
      adminClient(),
      magicLinkClient(),
      genericOAuthClient(),
    ],
  }) as unknown as AuthClient;
}
